import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, Button, Paper, Typography, Avatar, Alert } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { chatService } from '../../services/chatService';
import { ChatDTO, RequestCreateChatDTO } from '../../types/chat';
import { ChatMessageDTO, RequestCreateChatMessageDTO } from '../../types/chat-message';
import { ChatScope, ChatType, ChatMessageType } from '../../types/chat-enums';
import { useAuth } from '../../contexts/AuthContext';
import { UserType } from '../../types/auth';
import moment from 'moment';

interface TaskChatProps {
    taskId: string;
    chatType: ChatType; // Добавляем тип чата
    chatScope: ChatScope; // Добавляем область видимости чата
    initiatorId?: string; // ID инициатора (может быть managerId или userId)
}

const TaskChat: React.FC<TaskChatProps> = ({ taskId, chatType, chatScope, initiatorId }) => {
    const [chat, setChat] = useState<ChatDTO | null>(null);
    const [messages, setMessages] = useState<ChatMessageDTO[]>([]);
    const [newMessage, setNewMessage] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const chatMessagesEndRef = useRef<HTMLDivElement>(null);
    const { state } = useAuth();
    const authenticatedUser = state.user;
    const currentUserId = authenticatedUser?.type === UserType.USER ? authenticatedUser.user?.id : undefined;
    const currentManagerId = authenticatedUser?.type === UserType.MANAGER ? authenticatedUser.manager?.id : undefined;

    useEffect(() => {
        const fetchChat = async () => {
            try {
                let fetchedChat = await chatService.getChatByContextEntity(taskId, ChatScope.Task);
                if (!fetchedChat) {
                    setChat(null);
                    setMessages([]);
                } else {
                    setChat(fetchedChat);
                    const fetchedMessages = await chatService.getMessages(fetchedChat.id);
                    setMessages(fetchedMessages);
                }
                setError(null); // Сбрасываем ошибку, если загрузка прошла успешно
            } catch (err: any) {
                // Если 404, это означает, что чата нет, это не ошибка, а ожидаемое поведение
                if (err.response && err.response.status === 404) {
                    setChat(null);
                    setMessages([]);
                    setError(null); // Сбрасываем ошибку, так как это не настоящая ошибка
                } else {
                    setError(err.response?.data?.message || err.message || 'Не удалось загрузить чат.');
                    console.error('Error fetching chat:', err);
                }
            } finally {
                setLoading(false);
            }
        };

        if (taskId && (currentUserId || currentManagerId)) {
            fetchChat();
        } else if (!taskId) {
            setError("ID задачи не предоставлен.");
            setLoading(false);
        } else if (!currentUserId && !currentManagerId) {
            setError("Пользователь не авторизован.");
            setLoading(false);
        }
    }, [taskId, currentUserId, currentManagerId]);

    useEffect(() => {
        if (chatMessagesEndRef.current) {
            chatMessagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    const handleSendMessage = async () => {
        if (!newMessage.trim()) return; // Проверяем только текст сообщения

        let currentChat = chat;
        if (!currentChat) {
            try {
                // Создаем чат перед отправкой первого сообщения
                const createChatRequest: RequestCreateChatDTO = {
                    name: `Чат по задаче ${taskId.substring(0, 8)}`,
                    type: ChatType.Group,
                    scope: ChatScope.Task,
                    taskId: taskId, // Используем taskId напрямую
                    isPrivate: false,
                    participantUserIds: currentUserId ? [currentUserId] : [],
                    participantManagerIds: currentManagerId ? [currentManagerId] : [],
                };
                currentChat = await chatService.createChat(createChatRequest);
                setChat(currentChat);
                setError(null); // Сбрасываем ошибку после успешного создания чата
            } catch (err: any) {
                setError('Не удалось создать чат: ' + (err.response?.data?.message || err.message));
                console.error('Error creating chat:', err);
                return;
            }
        }

        if (!currentChat?.id) return; // Проверяем, что ID чата доступен

        const request: RequestCreateChatMessageDTO = {
            chatId: currentChat.id,
            content: newMessage,
            type: ChatMessageType.Text,
        };

        try {
            const sentMessage = await chatService.sendMessage(request);
            setMessages(prevMessages => [...prevMessages, sentMessage]);
            setNewMessage('');
            setError(null); // Сбрасываем ошибку после успешной отправки
        } catch (err: any) {
            setError('Не удалось отправить сообщение: ' + (err.response?.data?.message || err.message));
            console.error('Error sending message:', err);
        }
    };
    
    return (
        <Box>
            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}><Typography>Загрузка чата...</Typography></Box>}
            {!loading && error && <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{error}</Alert>}
            {!loading && !error && !chat && (
                <Alert severity="info" sx={{ mt: 2, mb: 2 }}>Куратор уже ждет вопроса.</Alert>
            )}
            {!loading && chat && (
                <Box sx={{ height: 400, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mb: 2 }}>
                    {messages.length === 0 ? (
                        <Typography color="text.secondary">Сообщений пока нет.</Typography>
                    ) : (
                        messages.map((msg, index) => (
                            <Box key={msg.id || index} sx={{ display: 'flex', mb: 1, alignItems: 'flex-start' }}>
                                <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: msg.fromUserId === currentUserId || msg.fromManagerId === currentManagerId ? 'primary.main' : 'secondary.main' }}>
                                    {msg.fromUser?.fullName?.[0] || msg.fromManager?.fullName?.[0] || '?'.toUpperCase()}
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {msg.fromUser?.fullName || msg.fromManager?.fullName || 'Неизвестный'} &bull; {moment(msg.createdAt).format('HH:mm DD.MM.YYYY')}
                                    </Typography>
                                    <Typography variant="body2">{msg.content}</Typography>
                                </Box>
                            </Box>
                        ))
                    )}
                    <div ref={chatMessagesEndRef} />
                </Box>
            )}
            {!loading && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder="Напишите сообщение..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSendMessage();
                            }
                        }}
                    />
                    <Button
                        variant="contained"
                        endIcon={<SendIcon />}
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim() || (!currentUserId && !currentManagerId) || loading} // Кнопка активна, если есть текст, пользователь авторизован и не грузится
                    >
                        Отправить
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default TaskChat;
